import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ListScreen, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { ConfirmDialog, Dialog } from '../components/ui/dialog'
import { Field, Input, Select } from '../components/ui/field'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip } from '../components/ui/tone'
import { ApiError, api, type SuppressionEntry, type SuppressionReason } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Suppressions (§32) — who must never be emailed.
 *
 * The one list in the product where being wrong has a named victim. Every other screen can be
 * re-run; an email to someone who asked never to hear from you again cannot be recalled.
 *
 * So removal is the designed act, not the accident: it takes a written reason of at least eight
 * characters, which §57 requires to be auditable, and the API enforces the same minimum so pasting
 * a request past this form gains nothing.
 */

/**
 * The enum is the wire format and is never shown. Each reason carries the sentence that actually
 * decides things — the difference between "not interested" and "contacted too often" is whether a
 * colleague may ever write to this person again.
 */
const REASONS: Array<{ value: SuppressionReason; label: string; hint: string }> = [
  { value: 'UNSUBSCRIBED', label: 'Unsubscribed', hint: 'They asked to stop, by any route.' },
  {
    value: 'SPAM_COMPLAINT',
    label: 'Spam complaint',
    hint: 'They reported it. Never contact again, from any campaign.',
  },
  { value: 'HARD_BOUNCE', label: 'Hard bounce', hint: 'The address does not exist.' },
  { value: 'NOT_INTERESTED', label: 'Not interested', hint: 'They replied no.' },
  {
    value: 'LEGAL_RESTRICTION',
    label: 'Legal restriction',
    hint: 'A jurisdiction, or a request with legal weight.',
  },
  {
    value: 'MANUAL_SUPPRESSION',
    label: 'Added by hand',
    hint: 'An operator decided, for a reason of their own.',
  },
  {
    value: 'EXCESSIVE_PREVIOUS_CONTACT',
    label: 'Contacted too often',
    hint: 'We have already asked enough times.',
  },
  { value: 'INVALID_ADDRESS', label: 'Invalid address', hint: 'Malformed, or a role account.' },
  {
    value: 'ABUSE_CONTACT',
    label: 'Abuse contact',
    hint: 'An address that exists to receive complaints.',
  },
]

const REASON_LABEL = Object.fromEntries(REASONS.map((r) => [r.value, r.label]))

/**
 * How far a suppression reaches, and the field most likely to be misread. `ORG_GLOBAL` is the one
 * that matters: it stops every campaign this organization will ever run.
 */
const SCOPE_LABEL: Record<string, string> = {
  ORG_GLOBAL: 'everywhere',
  CAMPAIGN: 'one campaign',
  JOURNAL: 'one journal',
}

export function SuppressionsScreen() {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<SuppressionEntry | null>(null)
  const [error, setError] = useState<string | null>(null)

  const entries = useQuery({
    queryKey: ['suppression'],
    queryFn: () => api.get<SuppressionEntry[]>('/suppression'),
    enabled: can('manage_suppression'),
  })

  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.del(`/suppression/${id}`, { reason }),
    onSuccess: async () => {
      setRemoving(null)
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['suppression'] })
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : 'Could not remove'),
  })

  if (!can('manage_suppression')) {
    return (
      <Screen>
        <ScreenHeader title="Suppressions" description="Addresses this organization must never email." />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to the suppression list"
            body="Viewing it requires the manage_suppression permission. Ask an administrator of your organization."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (entries.data ?? []).filter(
    (entry) => !search || entry.emailNormalized.toLowerCase().includes(search.toLowerCase()),
  )
  const total = entries.data?.length ?? 0

  return (
    <>
      <ListScreen
        title="Suppressions"
        subtitle="Addresses this organization must never email. Checked immediately before every send, never only at import."
        query={search}
        onQuery={setSearch}
        queryPlaceholder="Filter addresses"
        savedViews={[]}
        primary={
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            Suppress an address
          </Button>
        }
        hint={
          total > 0
            ? `${total.toLocaleString()} ${total === 1 ? 'address' : 'addresses'}. Adding one twice is a no-op; a wider scope replaces a narrower one.`
            : undefined
        }
      >
        {entries.isLoading ? (
          <TableSkeleton columns={4} />
        ) : entries.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="The suppression list could not be loaded"
            body="Nothing should be sent until it can be read — an unreadable suppression list is not an empty one (§49)."
            action={<Button onClick={() => void entries.refetch()}>Try again</Button>}
          />
        ) : rows.length > 0 ? (
          <Table>
            <THead>
              <Th>Address</Th>
              <Th width={140}>Reach</Th>
              <Th width={200}>Reason</Th>
              <Th width={120}>Since</Th>
              <Th width={100} align="right">
                <span className="sr-only">Actions</span>
              </Th>
            </THead>
            <TBody>
              {rows.map((entry) => (
                <Tr key={entry.id}>
                  <Td mono>{entry.emailNormalized}</Td>
                  <Td>
                    {/* The widest scope has to be visible at a glance. */}
                    <Chip tone={entry.scope === 'ORG_GLOBAL' ? 'bad' : 'neutral'}>
                      {SCOPE_LABEL[entry.scope] ?? entry.scope}
                    </Chip>
                  </Td>
                  <Td className="text-muted">{REASON_LABEL[entry.reason] ?? entry.reason}</Td>
                  <Td className="text-muted">
                    {new Date(entry.suppressedAt).toLocaleDateString()}
                  </Td>
                  <Td align="right">
                    <Button
                      size="sm"
                      variant="quiet"
                      icon="trash"
                      onClick={() => setRemoving(entry)}
                    >
                      Remove
                    </Button>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : search ? (
          <Blank
            icon="noResults"
            title={`Nothing matches “${search}”.`}
            action={<Button onClick={() => setSearch('')}>Clear filter</Button>}
          />
        ) : (
          <Blank
            icon="suppressions"
            title="Nobody is suppressed yet."
            body="Unsubscribes and hard bounces land here on their own. An empty list is the normal state before the first campaign."
          />
        )}
      </ListScreen>

      <AddDialog open={adding} onClose={() => setAdding(false)} />

      <ConfirmDialog
        open={removing !== null}
        title="Make this address contactable again?"
        body={
          removing && (
            <>
              <span className="font-mono">{removing.emailNormalized}</span> was suppressed on{' '}
              {new Date(removing.suppressedAt).toLocaleDateString()} —{' '}
              {(REASON_LABEL[removing.reason] ?? removing.reason).toLowerCase()}. Removing this
              entry means campaigns may email them again.
            </>
          )
        }
        confirmLabel="Remove suppression"
        requireReason
        reasonLabel="Why is this being removed?"
        reasonPlaceholder="they asked to be re-added by email on 3 March"
        pending={remove.isPending}
        error={error}
        onCancel={() => {
          setRemoving(null)
          setError(null)
        }}
        onConfirm={(reason) => removing && remove.mutate({ id: removing.id, reason })}
      />
    </>
  )
}

function AddDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState<SuppressionReason>('UNSUBSCRIBED')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: () => api.post('/suppression', { email, reason }),
    onSuccess: async () => {
      setEmail('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['suppression'] })
      onClose()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : 'Could not suppress'),
  })

  const selected = REASONS.find((r) => r.value === reason)

  return (
    <Dialog
      open={open}
      title="Suppress an address"
      description="A wider scope replaces a narrower one. The reverse is refused."
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!email || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Suppressing…' : 'Suppress'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Email" error={error}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              placeholder="someone@uni.edu"
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        {/* The chosen reason explains itself. Which one is picked changes how permanent this is,
            and nothing else on the screen says so. */}
        <Field label="Reason" hint={selected?.hint}>
          {(props) => (
            <Select
              {...props}
              value={reason}
              onChange={(e) => setReason(e.target.value as SuppressionReason)}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  )
}
